// Ce que le joueur prépare avant d'aller au practice.
//
// Un EXERCICE est ce qu'on fait avec une balle ; une SÉANCE est une suite
// d'exercices, écrite à l'avance. L'API appelle « session » un exercice
// exécuté — nom hérité du schéma — mais l'interface, elle, dit exercice.

import { api } from './api.ts';
import type { TrainingModeCode } from '@golf/common';

export interface ClubType {
    id: number;
    category: string;
    code: string;
    label: string;
    defaultLoft: number | null;
    typicalCarryM: number | null;
    /** Le joueur possède un club de ce type. Grise, n'interdit pas. */
    owned: boolean;
}

/** La nature d'un atelier.
 *
 *  `balls` : une série de balles, notée balle par balle. `course` : trois trous
 *  sur un vrai parcours, jugés à la moyenne au par — rien à saisir au practice,
 *  et donc un écran de déroulement différent. */
export type ExerciseKind = 'balls' | 'course';

/** Ce qu'un exercice a de commun, qu'il soit à soi ou fédéral. */
export interface ExerciceRegles {
    kind: ExerciseKind;
    /** Le règlement long, affiché avant de commencer. */
    criteria: string | null;
    /** Ce qui vaut UN POINT, dit en toutes lettres — « Rentrée du premier
     *  coup ». Quand il est là, l'écran de saisie abandonne les sept issues de
     *  balle pour deux réponses, et compte des points. */
    successLabel: string | null;
    courseMinHoles: number | null;
    courseBestHoles: number | null;
    courseMaxAvgOverPar: number | null;
}

export interface CustomExercise extends ExerciceRegles {
    id: string;
    name: string;
    description: string | null;
    /** `null` = libre. */
    ballsPerRound: number | null;
    rounds: number | null;
    clubTemplateIds: number[];
    /** Livré avec l'application : visible de tous, modifiable par personne. */
    officiel: boolean;
    lastSessionAt: string | null;
    sessionCount: number | null;
    /** `numeric` de Postgres : arrive en chaîne. */
    successRate: string | null;
}

export interface WorkoutItem {
    id: string;
    position: number;
    mode: TrainingModeCode | null;
    /** Troisième nature d'étape, exclusive des deux autres. */
    isPutting: boolean;
    exerciseId: string | null;
    exerciseName: string | null;
    ballsPerRound: number | null;
    rounds: number | null;
    kind: ExerciseKind | null;
    successLabel: string | null;
}

export interface Workout {
    id: string;
    /** Livrée avec l'application : le programme fédéral, que chacun peut lancer
     *  sans pouvoir le remanier. */
    officiel: boolean;
    name: string;
    description: string | null;
    items: WorkoutItem[];
    lastRunAt: string | null;
    /** La dernière exécution terminée, pour ouvrir son bilan d'un clic. */
    lastRunId: string | null;
    successRate: string | null;
    runCount: number | null;
    /** Le plan se note-t-il en points, et sur combien ? */
    pointsTarget: number | null;
    pointsMax: number;
    /** Le total du dernier passage, quand la séance se note en points. */
    lastRunPoints: number | null;
}

export interface RunItem extends ExerciceRegles {
    position: number;
    mode: TrainingModeCode | null;
    isPutting: boolean;
    exerciseId: string | null;
    exerciseName: string | null;
    exerciseDescription: string | null;
    ballsPerRound: number | null;
    rounds: number | null;
    clubTemplateIds: number[];
    /** L'exercice déjà exécuté à ce rang, s'il y en a un. */
    sessionId: string | null;
    sessionStatus: string | null;
    /** Son résultat, pour que la liste dise autre chose que « fait ». */
    successRate: string | null;
    shots: number | null;
    /** Les balles centrées — les POINTS de l'atelier — et son barème. */
    center: number | null;
    pointsMax: number | null;
    /* L'étape de parcours ne passe pas par les colonnes ci-dessus : elle n'a ni
       balles ni taux. Son résultat vient à côté, et `kind` dit lequel lire. */
    courseRoundId: string | null;
    courseHolesCounted: number | null;
    courseAvgOverPar: number | null;
    coursePassed: boolean | null;
}

export interface RunState {
    run: {
        id: string;
        name: string;
        status: 'in_progress' | 'completed';
        startedAt: string;
        endedAt: string | null;
        /** Le plan se note-t-il en points ? Le déroulé compte alors en points
         *  dès le premier atelier, et non seulement au bilan. */
        pointsTarget: number | null;
        workoutId: string | null;
    } | null;
    items: RunItem[];
}

export interface ExerciseDraft {
    name: string;
    description?: string | null;
    ballsPerRound?: number | null;
    rounds?: number | null;
    clubTemplateIds?: number[];
}

export interface RunTotals {
    shots: number;
    center: number;
    /** Les points, bornés au barème de chaque atelier. Absents des séances
     *  ordinaires, où rien ne les borne — le serveur les rend quand même. */
    points?: number;
    exercises: number;
    seconds: number;
    successRate: number | null;
}

export interface RunSummaryItem {
    position: number;
    /** L'un des deux est renseigné, jamais les deux : une étape porte un
     *  exercice personnalisé OU un mode intégré. */
    exerciseName: string | null;
    /** L'exercice lui-même, pour pouvoir le reproposer. */
    exerciseId: string | null;
    mode: string | null;
    sessionId: string;
    shots: number;
    /** Les balles centrées — les POINTS de l'atelier — et son barème. */
    center: number;
    pointsMax: number | null;
    successRate: string | null;
    /** Le même rang, la fois précédente du même plan. */
    previousRate: string | null;
}

export interface RunSummaryClub {
    clubId: string;
    label: string;
    brand: string | null;
    nickname: string | null;
    category: string;
    loftDeg: string | null;
    shots: number;
    center: number;
    successRate: string | null;
    /** Le même club, la fois précédente du même plan. */
    previousRate: string | null;
    distanceAvgM: string | null;
}

export interface RunSummary {
    run: {
        id: string; name: string; status: string;
        startedAt: string; endedAt: string | null; workoutId: string | null;
        /** Ce que le joueur a écrit après coup : ressentis, douleurs, météo. */
        notes: string | null;
    };
    totals: RunTotals;
    /** La fois précédente du MÊME plan, ou `null` s'il n'y en a pas eu. */
    previous: RunTotals | null;
    /** Nombre d'étapes prévues au plan, terminées ou non. */
    plannedItems: number;
    /** Les notes de la fois précédente du même plan. */
    previousNotes: string | null;
    items: RunSummaryItem[];
    /** Les étapes de PARCOURS du plan, faites ou non. Elles n'ouvrent aucune
     *  séance d'entraînement, donc rien ne les ramenait dans `items` — et le
     *  bilan pouvait annoncer un examen réussi sans parcours validé. */
    courseSteps: {
        position: number;
        name: string | null;
        passed: boolean | null;
        holesCounted: number | null;
        avgOverPar: string | null;
        maxAvgOverPar: string | null;
    }[];
    clubs: RunSummaryClub[];
    faults: { outcome: string; n: number }[];
    /** L'objectif du plan, et le total qu'il met en jeu. `pointsTarget` nul :
     *  la séance ne se note pas en points, et le bilan s'en tient au taux. */
    pointsTarget: number | null;
    pointsMax: number;
    completionNote: string | null;
    /** Les POINTS du passage, calculés par le serveur : bornés au barème de
     *  chaque atelier, et une seule séance retenue par rang. Ne pas les
     *  recalculer à l'écran — une somme de balles centrées ignore les deux. */
    points: number;
    previousPoints: number | null;
}

/** Enregistre les notes d'une séance. Une chaîne vide les efface. */
export const setRunNotes = (id: string, notes: string | null) =>
    api<void>(`/planning/runs/${id}`, { method: 'PATCH', body: { notes } });

export const getRunSummary = (id: string) =>
    api<RunSummary>(`/planning/runs/${id}/summary`);

export const listClubTypes = () =>
    api<{ types: ClubType[] }>('/planning/club-types').then((r) => r.types);

export const listExercises = () =>
    api<{ exercises: CustomExercise[] }>('/planning/exercises').then((r) => r.exercises);

export const createExercise = (body: ExerciseDraft) =>
    api<{ id: string }>('/planning/exercises', { method: 'POST', body });

export const updateExercise = (id: string, body: Partial<ExerciseDraft>) =>
    api<void>(`/planning/exercises/${id}`, { method: 'PATCH', body });

export const archiveExercise = (id: string) =>
    api<void>(`/planning/exercises/${id}`, { method: 'DELETE' });

export const listWorkouts = () =>
    api<{ workouts: Workout[] }>('/planning/workouts').then((r) => r.workouts);

export const createWorkout = (body: { name: string; description?: string | null }) =>
    api<{ id: string }>('/planning/workouts', { method: 'POST', body });

export const updateWorkout = (id: string, body: { name?: string; description?: string | null }) =>
    api<void>(`/planning/workouts/${id}`, { method: 'PATCH', body });

export const archiveWorkout = (id: string) =>
    api<void>(`/planning/workouts/${id}`, { method: 'DELETE' });

/** Remplace la composition entière : ajouter, déplacer et retirer sont le même
 *  geste vu d'ici — on rend la liste telle qu'elle doit être. */
export const setWorkoutItems = (
    id: string,
    items: { mode?: TrainingModeCode | null; exerciseId?: string | null; isPutting?: boolean }[],
) => api<void>(`/planning/workouts/${id}/items`, { method: 'PUT', body: { items } });

export const startWorkout = (id: string) =>
    api<RunState>(`/planning/workouts/${id}/start`, { method: 'POST' });

/** Une étape est-elle FAITE ?
 *
 *  Trois natures d'étape, une seule question. Un exercice de balles et une
 *  partie de putting laissent une session terminée ; une étape de parcours ne
 *  laisse rien de tel — elle n'a pas de session du tout — mais un verdict.
 *
 *  À ÉCHEC COMPRIS : recaler à l'étape de parcours, c'est l'avoir passée. La
 *  séance doit pouvoir se clore dessus, et le bilan dire qu'elle n'est pas
 *  validée — plutôt que de rester ouverte à jamais sur une étape que le joueur
 *  a bel et bien faite.
 *
 *  La règle vivait en six exemplaires dans autant d'écrans, tous écrits avant
 *  qu'une troisième nature existe. Elle est ici, et nulle part ailleurs. */
export const etapeFaite = (
    i: Pick<RunItem, 'kind' | 'sessionStatus' | 'coursePassed'>,
): boolean => (i.kind === 'course' ? i.coursePassed != null : i.sessionStatus === 'completed');

/** Une étape du plan, telle qu'elle sert de LIGNE au tableau des passages :
 *  elle existe même quand un passage l'a sautée. */
export interface EtapePlan {
    position: number;
    name: string | null;
    kind: ExerciseKind | null;
    successLabel: string | null;
    /** Le barème de l'atelier — balles × séries. `null` sur une étape qui ne se
     *  note pas en points : un mode intégré, le putting, un parcours. */
    pointsMax: number | null;
    mode: string | null;
    isPutting: boolean;
}

export interface Passage {
    id: string;
    startedAt: string;
    endedAt: string | null;
    status: string;
    notes: string | null;
    /** Ce qu'a donné chaque rang. Les rangs sautés n'y figurent pas. */
    etapes: {
        position: number;
        points: number | null;
        shots: number | null;
        passed: boolean | null;
    }[];
}

export interface PassagesSeance {
    workout: {
        id: string;
        name: string;
        /** Le total à atteindre pour réussir la séance. `null` sur une séance
         *  ordinaire : on ne réussit pas un practice, on le fait. */
        pointsTarget: number | null;
        /** Ce qui reste à faire HORS de l'application — rien dans les données
         *  ne peut le deviner. */
        completionNote: string | null;
    };
    etapes: EtapePlan[];
    /** Du plus récent au plus ancien : c'est l'ordre d'une liste. L'écran
     *  retourne la suite pour ses figures, où le temps va vers la droite. */
    passages: Passage[];
}

/** Tous les passages d'une séance, atelier par atelier.
 *
 *  Le bilan compare deux exécutions ; celui-ci les montre toutes, parce qu'une
 *  progression ne se lit pas sur deux points — et parce que la liste des
 *  séances ne menait qu'au dernier bilan. */
export const getPassages = (workoutId: string) =>
    api<PassagesSeance>(`/planning/workouts/${workoutId}/runs`);

export const getCurrentRun = () => api<RunState>('/planning/runs/current');

/** Valide l'étape de parcours sur un tour déjà joué.
 *
 *  Le calcul — meilleurs trous, moyenne au par, verdict — se fait sur le
 *  serveur : un examen dont le résultat arriverait tout calculé du téléphone
 *  n'en serait plus un. On envoie le tour, on reçoit le verdict. */
export const validerEtapeParcours = (runId: string, position: number, roundId: string) =>
    api<{ ok: true; passed: boolean; holes: number; avg: number }>(
        `/planning/runs/${runId}/course/${position}`, { method: 'POST', body: { roundId } },
    );

export const finishRun = (id: string, status: 'completed' | 'aborted') =>
    api<RunState | void>(`/planning/runs/${id}/finish`, { method: 'POST', body: { status } });

/** Les choix proposés pour le nombre de balles et de séries.
 *
 *  `null` est « libre », et il est en tête : c'est le cas le plus fréquent, et
 *  celui qui n'impose rien à quelqu'un qui découvre le compositeur. */
export const BALL_CHOICES: (number | null)[] = [null, 1, 3, 5, 10];
/* Dix séries en bout de liste, comme dix balles.
 *
 * Ce n'est pas seulement pour remplir la cinquième case restée vide sur une
 * grille qui en compte cinq — même si le trou se voyait. C'est aussi qu'une
 * série de dix passes courtes est un exercice ordinaire, et qu'il fallait
 * jusqu'ici cinq appuis sur « + » pour y arriver depuis 5. */
export const ROUND_CHOICES: (number | null)[] = [null, 1, 3, 5, 10];

export const boundLabel = (v: number | null): string => (v === null ? 'Libre' : String(v));
